import { TypedDocumentNode, gql } from '@apollo/client';
import { compareAsc } from 'date-fns';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Navigate, generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  FixtureState,
  GameStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { Calendar } from '@sorare/core/src/atoms/dates/Calendar';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { BoxLink } from '@sorare/core/src/atoms/ui/Box';
import { EmptyGames } from '@sorare/core/src/components/EmptyState/EmptyGames';
import { ToggleFilter } from '@sorare/core/src/components/live/Filters/ToggleFilter';
import { NBA_PLAY_PRO } from '@sorare/core/src/constants/__generated__/routes';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useKeyboardSelection } from '@sorare/core/src/hooks/useKeyboardSelection';
import useLocalStorage, {
  STORAGE,
} from '@sorare/core/src/hooks/useLocalStorage';
import { playerGameStatusLabels } from '@sorare/core/src/lib/glossary';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';

import { CardAvatar } from '@sorare/us-sports/src/components/cards/Avatar';
import { GameSelection } from '@sorare/us-sports/src/components/game/GameSelection';
import { calculateScoreWithBonus } from '@sorare/us-sports/src/lib/sports';

import { LiveGameQuarterStatus } from 'components/game/LiveGameQuarterStatus';

import { LiveGameSelectionView_NBAFixture } from './__generated__/index.graphql';

const Appearances = styled(Horizontal).attrs({ gap: 2, center: true })``;

const Appearance = styled(Vertical).attrs({ gap: 0, center: true })`
  & img {
    width: var(--quadruple-unit);
    aspect-ratio: 1;
    border-radius: 50%;
    object-fit: cover;
  }
  justify-content: stretch;
`;

const LeaderboardIcon = styled.img`
  height: var(--triple-unit);
`;

const GameBoxLink = styled(BoxLink)`
  scroll-margin-top: calc(var(--unit) + var(--current-stack-height));
`;

type CardInLineupType =
  NonNullable<LiveGameSelectionView_NBAFixture>['myLineups'][number]['cards'][number];

type GameType = {
  game: NonNullable<LiveGameSelectionView_NBAFixture>['games'][number];
  cardsInLineup: CardInLineupType[];
};

type Props = {
  currentFixture: LiveGameSelectionView_NBAFixture | null;
  gameIdFromRouteParams?: string;
  liveGameIdRoute: string;
};

const MAX_NUMBER_OF_CARDS_IN_APPEARANCE = 5;
export const LiveGameSelectionView = ({
  currentFixture,
  gameIdFromRouteParams,
  liveGameIdRoute,
}: Props) => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const navigate = useNavigate();

  const [onlyGamesWithLinedUpPlayers, setOnlyGamesWithLinedUpPlayers] =
    useLocalStorage<false | true>(
      STORAGE.showOnlyGamesWithLinedUpPlayers,
      true
    );

  const games = currentFixture?.games.map(game => ({ game }) as GameType) ?? [];

  const cardsInLineup = [
    ...(currentFixture?.myLineups
      .filter(l => !l.leaderboard.isTraining)
      .flatMap(l => l.cards as CardInLineupType[]) ?? []),
  ];

  const gamesById = games.reduce<Record<string, GameType>>((map, g) => {
    const inMap = map[g.game.id];
    const existingCards = inMap?.cardsInLineup || [];

    const currentUserCardsInGame = cardsInLineup
      .filter(
        c =>
          c.playerInFixture?.status.upcomingGames.find(
            game => game.id === g.game.id
          ) ||
          c.playerInFixture?.status.gameStats.find(
            gS => gS.game.id === g.game.id
          )
      )
      .filter(c => !existingCards.find(ec => ec.card.id === c.card.id));

    return {
      ...map,
      [g.game.id]: {
        ...(inMap || {}),
        ...g,
        cardsInLineup: [
          ...(inMap?.cardsInLineup || []),
          ...currentUserCardsInGame,
        ],
      },
    };
  }, {});

  const filteredGames = onlyGamesWithLinedUpPlayers
    ? Object.values(gamesById).filter(g => g.cardsInLineup.length > 0)
    : Object.values(gamesById);

  const sortedGames = filteredGames.sort((a, b) =>
    compareAsc(a.game.startDate, b.game.startDate)
  );

  const selectedIndex = sortedGames.findIndex(
    ({ game }) => game.id === gameIdFromRouteParams
  );

  const selectItemAtIndex = (index: number) => {
    const id = sortedGames[index]?.game.id;
    navigate(generatePath(liveGameIdRoute, { id }), { replace: true });
  };

  const { highlightedItemIndex: highlightedIndex, setHighlightedItemIndex } =
    useKeyboardSelection({
      selectedIndex,
      selectItemAtIndex,
      nbItems: sortedGames.length,
    });

  if (sortedGames.length === 0) {
    return (
      <>
        <FullWidthContainer.NoPadding>
          <ToggleFilter
            label={
              <FormattedMessage
                id="Filters.showOnlyGamesWithLinedUpPlayers"
                defaultMessage="Only games with lined up players"
              />
            }
            value={onlyGamesWithLinedUpPlayers}
            onChange={() => {
              setOnlyGamesWithLinedUpPlayers(val => !val);
            }}
          />
        </FullWidthContainer.NoPadding>
        <EmptyGames
          ctaLink={NBA_PLAY_PRO}
          upcoming={currentFixture?.fixtureState === FixtureState.opened}
        />
      </>
    );
  }

  // select either the first "live" game, otherwise the last "past" game
  let expandDate: Date | undefined;
  if (
    (!gameIdFromRouteParams ||
      !sortedGames.some(({ game }) => game.id === gameIdFromRouteParams)) &&
    sortedGames?.[0]?.game.id &&
    isDesktopAndAbove
  ) {
    const firstLive =
      sortedGames.find(g => g.game.status === GameStatus.playing) ||
      sortedGames.filter(g => g.game.status === GameStatus.played).pop();
    expandDate = firstLive?.game.startDate;
    return (
      <Navigate
        to={generatePath(liveGameIdRoute, {
          id: (firstLive || sortedGames?.[0])?.game.id,
        })}
        replace
      />
    );
  }

  const items = sortedGames.map((g, index) => {
    const sortedCardsInLineup = sortByRarity(
      g.cardsInLineup,
      c => c.card.rarityTyped
    );
    return {
      date: g.game.startDate,
      selected: selectedIndex === index,
      highlighted: highlightedIndex === index,
      node: (
        <GameBoxLink
          key={g.game.id}
          to={generatePath(liveGameIdRoute, {
            id: g.game.id,
          })}
          selected={selectedIndex === index || highlightedIndex === index}
        >
          <GameSelection
            game={g.game}
            liveGameTimeStatus={<LiveGameQuarterStatus game={g.game} />}
            extra={
              sortedCardsInLineup.length > 0 ? (
                <Appearances>
                  {sortedCardsInLineup
                    .slice(0, MAX_NUMBER_OF_CARDS_IN_APPEARANCE)
                    .map(cardInLineup => {
                      const currentGameStats =
                        cardInLineup.playerInFixture.status.gameStats.find(
                          gS => gS.game.id === g.game.id
                        );
                      return (
                        <div
                          style={{
                            opacity: currentGameStats?.playedInGame ? 1 : '0.2',
                          }}
                          key={cardInLineup.card.id}
                        >
                          <Tooltip
                            title={
                              <Horizontal>
                                <LeaderboardIcon
                                  src={
                                    cardInLineup.lineup.leaderboard
                                      .monochromeIconImageUrl
                                  }
                                  alt=""
                                />
                                {cardInLineup.lineup.leaderboard.displayName}
                              </Horizontal>
                            }
                          >
                            <Appearance>
                              <CardAvatar
                                card={cardInLineup.card}
                                maxWidth={40}
                                // showBoost={cardInLineup.boost?.id}
                              />
                              {[GameStatus.playing, GameStatus.played].includes(
                                g.game.status
                              ) && (
                                <LabelS as="div">
                                  {currentGameStats?.playedInGame ? (
                                    <FormattedNumber
                                      maximumFractionDigits={0}
                                      value={
                                        calculateScoreWithBonus(
                                          currentGameStats?.score,
                                          cardInLineup.totalBonus
                                        ).score
                                      }
                                    />
                                  ) : (
                                    <FormattedMessage
                                      {...playerGameStatusLabels.did_not_play_short}
                                    />
                                  )}
                                </LabelS>
                              )}
                            </Appearance>
                          </Tooltip>
                        </div>
                      );
                    })}
                  {sortedCardsInLineup.length >
                    MAX_NUMBER_OF_CARDS_IN_APPEARANCE && (
                    <LabelM as="div" color="var(--c-neutral-700)" bold>
                      {`+${
                        sortedCardsInLineup.length -
                        MAX_NUMBER_OF_CARDS_IN_APPEARANCE
                      }`}
                    </LabelM>
                  )}
                </Appearances>
              ) : null
            }
          />
        </GameBoxLink>
      ),
    };
  });

  const startDate = currentFixture?.startDate || new Date();
  const endDate = currentFixture?.endDate || new Date();

  return (
    <ContentWithStickyHeader
      header={
        <FullWidthContainer.NoPadding>
          <ToggleFilter
            label={
              <FormattedMessage
                id="Filters.showOnlyGamesWithLinedUpPlayers"
                defaultMessage="Only games with lined up players"
              />
            }
            value={onlyGamesWithLinedUpPlayers}
            onChange={() => {
              setOnlyGamesWithLinedUpPlayers(val => !val);
            }}
          />
        </FullWidthContainer.NoPadding>
      }
    >
      <Calendar
        startDate={startDate}
        endDate={endDate}
        items={items}
        expandDate={expandDate}
        collapsible
        selectItemAtIndex={selectItemAtIndex}
        highlightItemAtIndex={setHighlightedItemIndex}
      />
    </ContentWithStickyHeader>
  );
};

LiveGameSelectionView.fragments = {
  NBAFixture: gql`
    fragment LiveGameSelectionView_NBAFixture on NBAFixture {
      slug
      startDate
      endDate
      fixtureState
      games {
        id
        status
        startDate
        ...GameSelection_GameInterface
        ...LiveGameQuarterStatus_NBAGame
      }
      myLineups {
        id
        leaderboard {
          slug
          displayName
          isTraining
        }
        cards {
          totalBonus
          lineup {
            id
            leaderboard {
              slug
              displayName
              monochromeIconImageUrl
            }
          }
          playerInFixture {
            player {
              slug
            }
            fixture {
              slug
            }
            status {
              upcomingGames {
                id
              }
              gameStats {
                playedInGame
                game {
                  id
                }
                score
              }
            }
          }
          card {
            id
            slug
            rarityTyped
            pictureUrl
            avatarUrl: pictureUrl(derivative: "squared")
            anyPlayer {
              slug
              displayName
            }
            ...CardAvatar_anyCard
          }
        }
        games {
          game {
            id
          }
        }
      }
    }
    ${CardAvatar.fragments.anyCard}
    ${GameSelection.fragments.GameInterface}
    ${LiveGameQuarterStatus.fragments.NBAGame}
  ` as TypedDocumentNode<LiveGameSelectionView_NBAFixture>,
};
